/**
 * @file SamplingSketchPad.tsx
 * @description 採樣位置示意圖手繪畫板（採樣送檢 · PPT S56/S57）。
 *
 * 背景：紙本採樣單上有「田區採樣位置手繪示意圖」欄位，數位化後由本畫板承接：
 * 稽核員在 canvas 上手繪田區與採樣點，或先上傳現成底圖（地籍圖/空拍圖）再標注。
 *
 * 功能：
 * - 畫筆 / 橡皮擦（白底畫布上以白色粗筆實現擦除）
 * - 撤銷一步（快照棧，容量上限見 PAD_UNDO_LIMIT）/ 清除重畫
 * - 上傳底圖：等比縮放置中（contain）鋪為背景後再標注；會覆蓋既有筆跡（可撤銷）
 * - 完成後輸出 PNG Blob → uploadFile 直傳 OSS 落 files 表 → 回調 fileId 給父層
 *
 * 純邏輯（撤銷棧 / contain 排版 / 座標映射）抽在 sampling-sketch-core.ts 以便單測。
 *
 * 離線注意：輸出 PNG 需在線直傳 OSS，離線時父層（SamplesPanel）禁用畫板入口；
 * 本元件在上傳前再做一次在線兜底檢查。
 *
 * 另導出 SamplingSketchThumbnail：已有示意圖時的縮略圖展示（antd Image 可點擊放大），
 * 供 SamplesPanel 的 Drawer 使用。
 */

import { useRef, useState } from 'react';
import { Alert, Button, Image, Modal, Radio, Space, Typography, Upload } from 'antd';
import { Eraser, ImageUp, Pencil, RotateCcw, Trash2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { message } from '@/lib/antd-app';
import { apiClient } from '@/lib/api-client';
import { uploadFile, type UploadedFile } from '@/lib/file-upload';
import {
  computeContainRect,
  mapPointerToCanvas,
  popSnapshot,
  pushSnapshot,
} from './sampling-sketch-core';

// ─── 常數 ────────────────────────────────────────────────────────────────────

/** 畫布內部解析度（輸出 PNG 的像素尺寸；CSS 顯示寬 100% 自適應） */
const CANVAS_W = 800;
const CANVAS_H = 600;

/**
 * 撤銷快照棧容量上限。
 * 每筆快照為整幅 ImageData（800*600*4 ≈ 1.9MB），15 筆封頂約 28MB，
 * 在現場平板上是可接受的記憶體開銷。
 */
const PAD_UNDO_LIMIT = 15;

/** 畫筆樣式 */
const PEN_COLOR = '#1f2937';
const PEN_WIDTH = 3;
/** 橡皮擦（白底畫布 → 白色粗筆即擦除） */
const ERASER_COLOR = '#ffffff';
const ERASER_WIDTH = 24;

/** 工具類型 */
type Tool = 'pen' | 'eraser';

// ─── 畫板 Modal ──────────────────────────────────────────────────────────────

/** SamplingSketchPad props */
export interface SamplingSketchPadProps {
  /** Modal 是否開啟 */
  open: boolean;
  /** 所屬案件 ID（作為上傳文件的 owner 歸屬） */
  caseId: number;
  /** 關閉回調（取消或上傳成功後都會呼叫） */
  onClose: () => void;
  /** PNG 上傳成功回調；父層據此把 file.id 寫入 sample.sketchFileId */
  onUploaded: (file: UploadedFile) => void;
}

/**
 * 採樣位置示意圖手繪畫板 Modal。
 *
 * 使用方式：父層控制 open；使用者繪製完成點「完成並上傳」→
 * canvas 轉 PNG Blob → uploadFile 直傳 OSS → onUploaded(file) 回傳 fileId。
 */
export function SamplingSketchPad({ open, caseId, onClose, onUploaded }: SamplingSketchPadProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  /** 是否正在一筆繪製中（pointerdown 到 pointerup 之間） */
  const drawingRef = useRef(false);

  const [tool, setTool] = useState<Tool>('pen');
  /** 撤銷快照棧（棧頂在末端）；ImageData 不參與渲染，但用 state 驅動按鈕禁用態 */
  const [undoStack, setUndoStack] = useState<ImageData[]>([]);
  const [uploading, setUploading] = useState(false);

  /** 取得 2D context（畫板生命週期內 canvas 常駐 Modal 中） */
  const getCtx = (): CanvasRenderingContext2D | null => canvasRef.current?.getContext('2d') ?? null;

  /** 初始化 / 重置畫布為純白底（PNG 不留透明背景，列印友好） */
  const resetCanvas = () => {
    const ctx = getCtx();
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  };

  /** Modal 開啟動畫結束後初始化畫布（此時 canvas 已在 DOM 中） */
  const handleAfterOpenChange = (nowOpen: boolean) => {
    if (nowOpen) {
      resetCanvas();
      setUndoStack([]);
      setTool('pen');
    }
  };

  /** 繪製動作前推入撤銷快照（一筆一快照；清除/鋪底圖前也各推一筆） */
  const snapshotBeforeMutation = () => {
    const ctx = getCtx();
    if (!ctx) return;
    const snap = ctx.getImageData(0, 0, CANVAS_W, CANVAS_H);
    setUndoStack((prev) => pushSnapshot(prev, snap, PAD_UNDO_LIMIT));
  };

  // ── 指標事件：手繪 ──────────────────────────────────────────────────────────

  /** 把指標事件換算為 canvas 內部座標（CSS 寬 100% 自適應 → 需按比例映射） */
  const toCanvasPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    return mapPointerToCanvas(
      e.clientX,
      e.clientY,
      canvas.getBoundingClientRect(),
      CANVAS_W,
      CANVAS_H,
    );
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = getCtx();
    if (!ctx) return;
    // 落筆前先存快照 → 「撤銷一步」= 撤銷最近一整筆
    snapshotBeforeMutation();
    drawingRef.current = true;
    // 捕獲指標：拖出 canvas 邊界仍持續收到 move/up 事件
    e.currentTarget.setPointerCapture(e.pointerId);

    const { x, y } = toCanvasPoint(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = tool === 'pen' ? PEN_COLOR : ERASER_COLOR;
    ctx.lineWidth = tool === 'pen' ? PEN_WIDTH : ERASER_WIDTH;
    // 點一下也要出點：畫一段零長度線
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const ctx = getCtx();
    if (!ctx) return;
    const { x, y } = toCanvasPoint(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  // ── 工具列動作 ──────────────────────────────────────────────────────────────

  /** 撤銷一步：彈出棧頂快照還原畫布 */
  const handleUndo = () => {
    const ctx = getCtx();
    if (!ctx) return;
    const { top, rest } = popSnapshot(undoStack);
    if (!top) return;
    ctx.putImageData(top, 0, 0);
    setUndoStack(rest);
  };

  /** 清除：整幅重置為白底（可撤銷） */
  const handleClear = () => {
    snapshotBeforeMutation();
    resetCanvas();
  };

  /**
   * 上傳底圖：讀取本地圖片，等比縮放置中（contain）鋪為畫布背景。
   * 注意：會以白底 + 底圖重繪整幅畫布，覆蓋既有筆跡（已先存快照，可撤銷）。
   */
  const handleBackgroundUpload = (file: File) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const ctx = getCtx();
      if (!ctx) return;
      snapshotBeforeMutation();
      // 白底 + contain 置中鋪圖（不裁切不變形，留白居中）
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
      const rect = computeContainRect(img.naturalWidth, img.naturalHeight, CANVAS_W, CANVAS_H);
      ctx.drawImage(img, rect.x, rect.y, rect.width, rect.height);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      // 業務必要：底圖載入失敗需輸出錯誤日誌
      console.error('[sampling-sketch] background image load failed', { filename: file.name, err });
      message.error('底圖載入失敗，請確認圖片格式');
    };
    img.src = url;
    // 返回 false：阻止 antd Upload 的預設上傳行為（僅本地讀取鋪底）
    return false;
  };

  /** 完成並上傳：canvas → PNG Blob → uploadFile 直傳 OSS → 回調 fileId */
  const handleFinish = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // 在線兜底檢查：直傳 OSS 必須在線（入口已由父層禁用，此處防禦性再攔一次）
    if (!navigator.onLine) {
      message.warning('目前離線，示意圖需連線後才能上傳');
      return;
    }
    setUploading(true);
    /** PNG Blob → uploadFile 直傳 OSS（toBlob 回調需同步簽名，故拆出 async 函數） */
    const uploadBlob = async (blob: Blob | null) => {
      try {
        if (!blob) throw new Error('無法產生示意圖圖片');
        const file = await uploadFile(blob, {
          scope: 'case',
          ownerType: 'case',
          ownerId: caseId,
          filename: `sampling-sketch-${Date.now()}.png`,
          mimeType: 'image/png',
        });
        message.success('示意圖已上傳');
        onUploaded(file);
        onClose();
      } catch (err) {
        // 業務必要：上傳失敗需輸出錯誤日誌（caseId 上下文便於排查 OSS / 權限）
        console.error('[sampling-sketch] upload failed', { caseId, err });
        message.error(err instanceof Error ? err.message : '示意圖上傳失敗');
      } finally {
        setUploading(false);
      }
    };
    canvas.toBlob((blob) => void uploadBlob(blob), 'image/png');
  };

  return (
    <Modal
      title="繪製採樣位置示意圖"
      open={open}
      onCancel={onClose}
      afterOpenChange={handleAfterOpenChange}
      width={860}
      maskClosable={false}
      footer={
        <Space>
          <Button onClick={onClose} disabled={uploading}>
            取消
          </Button>
          <Button type="primary" loading={uploading} onClick={handleFinish}>
            完成並上傳
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="對應紙本採樣單的田區採樣位置手繪示意圖：可直接手繪，或先上傳底圖（地籍圖／空拍圖）再標注採樣點。"
          style={{ fontSize: 12 }}
        />
        {/* 工具列 */}
        <Space wrap>
          <Radio.Group
            value={tool}
            onChange={(e) => setTool(e.target.value as Tool)}
            optionType="button"
            buttonStyle="solid"
            size="small"
            options={[
              {
                value: 'pen',
                label: (
                  <Space size={4}>
                    <Pencil size={12} />
                    畫筆
                  </Space>
                ),
              },
              {
                value: 'eraser',
                label: (
                  <Space size={4}>
                    <Eraser size={12} />
                    橡皮擦
                  </Space>
                ),
              },
            ]}
          />
          <Button
            size="small"
            icon={<RotateCcw size={12} />}
            disabled={undoStack.length === 0}
            onClick={handleUndo}
          >
            撤銷
          </Button>
          <Button size="small" icon={<Trash2 size={12} />} onClick={handleClear}>
            清除
          </Button>
          <Upload
            accept="image/*"
            showUploadList={false}
            beforeUpload={handleBackgroundUpload}
            disabled={uploading}
          >
            <Button size="small" icon={<ImageUp size={12} />}>
              上傳底圖
            </Button>
          </Upload>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            上傳底圖會覆蓋既有筆跡（可撤銷）
          </Typography.Text>
        </Space>
        {/* 畫布：內部解析度固定 800×600，CSS 寬 100% 自適應 */}
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{
            width: '100%',
            aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
            border: '1px solid #d9d9d9',
            borderRadius: 8,
            cursor: 'crosshair',
            // 阻止觸控裝置上繪製時的頁面滾動 / 手勢
            touchAction: 'none',
            display: 'block',
            background: '#fff',
          }}
        />
      </Space>
    </Modal>
  );
}

// ─── 縮略圖展示 ──────────────────────────────────────────────────────────────

/** 後端 `/api/files/:id/download` 的回傳結構（successResponse 的 data 欄位） */
interface PresignedDownload {
  /** OSS 預簽名 URL（5 分鐘有效） */
  url: string;
  /** 有效期（秒） */
  expiresInSec: number;
}

/** SamplingSketchThumbnail props */
interface SketchThumbnailProps {
  /** files.id（採樣的 sketchFileId） */
  fileId: number;
}

/**
 * 採樣位置示意圖縮略圖：取 inline 預簽名 URL 後以 antd Image 展示（點擊放大）。
 *
 * 離線 / 取 URL 失敗時降級為文字提示（示意圖本體存 OSS，離線無法載入）。
 */
export function SamplingSketchThumbnail({ fileId }: SketchThumbnailProps) {
  const urlQ = useQuery({
    queryKey: ['file-inline-url', fileId],
    queryFn: async () => {
      const res = await apiClient.get<PresignedDownload>(
        `/api/files/${fileId}/download?disposition=inline`,
      );
      return res.url;
    },
    // 預簽名 URL 5 分鐘過期，快取 4 分鐘內復用、過期後重取
    staleTime: 4 * 60 * 1000,
    retry: 1,
  });

  if (urlQ.isLoading) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        示意圖載入中…
      </Typography.Text>
    );
  }
  if (urlQ.isError || !urlQ.data) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        無法載入示意圖（離線或連結過期），連線後重新開啟即可查看
      </Typography.Text>
    );
  }
  return (
    <Image
      src={urlQ.data}
      alt="採樣位置示意圖"
      width={200}
      style={{ border: '1px solid #f0f0f0', borderRadius: 6 }}
    />
  );
}
